import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Unified createTemplate call
export const createTemplate = createAsyncThunk(
  "templateCreation/createTemplate",
  async (templateData, { getState, rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      // You can adapt this endpoint or body as needed
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/templates/create`,
        templateData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Media upload for template headers
export const uploadTemplateMedia = createAsyncThunk(
  "templateCreation/uploadTemplateMedia",
  async (formData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/templates/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

const initialState = {
  selectedCategory: "Marketing",

  // Common fields
  templateId: "",
  language: "en",
  allowAutoCategoryChange: false,
  header: "none",
  headerData: null,
  body: "",
  bodyVariables: [],
  footer: "",

  // Which Buttons radio is selected: "none" | "buttons" | "carousel"
  buttonsMode: "none",

  // Buttons (used in both utility & marketing)
  buttons: {
    quickReplies: [],
    phoneButtons: [],
    linkButtons: [],
  },

  // Additional marketing-only fields (carousel, etc.)
  carouselMediaType: "image",
  carouselNumButtons: 1,
  carouselBtnOneType: "quickReply",
  carouselBtnTwoType: "link",
  carouselCards: [], // Each card has: mediaType, mediaUrl, body, bodyVariables, buttons

  // Authentication-specific fields
  codeDeliveryMethod: "COPY_CODE",
  addSecurityRecommendation: true,
  addExpiryTime: false,
  expiryMinutes: 10,
  components: [], // For storing the generated authentication components

  // Validation
  fieldsFlag: false,
  fieldsErrorMsg: "",
  shouldCreate: false,

  // Edit mode — set via startEditTemplate when the wizard is opened (at step 2)
  // to edit an existing template instead of creating a new one. templateId above
  // then holds the existing template's (immutable) name; editTemplateId holds its
  // Graph API id, which the edit endpoint targets.
  editMode: false,
  editTemplateId: null,

  // Async
  status: "idle",
  error: null,
  responseData: null,

  // Media upload status
  mediaUploadStatus: "idle",
  mediaUploadError: null,
};

const templateCreationSlice = createSlice({
  name: "templateCreation",
  initialState,
  reducers: {
    setSelectedCategory(state, action) {
      const newCategory = action.payload;
      if (state.selectedCategory !== newCategory) {
        Object.assign(state, initialState);
        state.selectedCategory = newCategory;
      }
    },
    resetToDefaults(state) {
      Object.assign(state, initialState);
    },
    // Prefills the form from an existing template (see templateEditFormState.js)
    // and flips editMode on, so TemplateSubmission calls the edit endpoint instead
    // of create. Resets everything else first so no stale create-flow data leaks in.
    startEditTemplate(state, action) {
      Object.assign(state, initialState, action.payload);
    },

    // Generic field updates
    updateFormData(state, action) {
      const { key, value } = action.payload;
      state[key] = value;
    },
    updateFieldsFlagAndMsg(state, action) {
      state.fieldsFlag = action.payload.fieldsFlag;
      state.fieldsErrorMsg = action.payload.fieldsErrorMsg;
    },
    setShouldCreate(state, action) {
      state.shouldCreate = action.payload;
    },
    setButtonsMode(state, action) {
      state.buttonsMode = action.payload;
    },

    // ================== Buttons (common) ==================
    // Quick Replies
    addQuickReply(state) {
      // No static text => label: ""
      state.buttons.quickReplies.push({ label: "" });
    },
    removeQuickReply(state, action) {
      state.buttons.quickReplies.splice(action.payload, 1);
    },
    updateQuickReply(state, action) {
      const { index, label } = action.payload;
      state.buttons.quickReplies[index].label = label;
    },

    // Phone Button
    addPhoneButton(state) {
      // No static text
      state.buttons.phoneButtons = [
        { label: "", countryCode: "", phoneNumber: "" },
      ];
    },
    removePhoneButton(state) {
      state.buttons.phoneButtons = [];
    },
    updatePhoneButton(state, action) {
      const { label, countryCode, phoneNumber } = action.payload;
      if (state.buttons.phoneButtons.length > 0) {
        const phoneBtn = state.buttons.phoneButtons[0];
        phoneBtn.label = label;
        phoneBtn.countryCode = countryCode;
        phoneBtn.phoneNumber = phoneNumber;
      }
    },

    // Link Buttons
    addLinkButton(state, action) {
      const newLinkButton = action.payload || {
        label: "",
        link: "https://",
        isLinkDynamic: false,
        dynamicVariableLabel: "",
      };
      state.buttons.linkButtons.push(newLinkButton);
    },
    removeLinkButton(state, action) {
      state.buttons.linkButtons.splice(action.payload, 1);
    },
    updateLinkButton(state, action) {
      const { index, label, link, isLinkDynamic, dynamicVariableLabel } =
        action.payload;
      const linkBtn = state.buttons.linkButtons[index];

      if (linkBtn) {
        if (label !== undefined) linkBtn.label = label;
        if (link !== undefined) linkBtn.link = link;
        if (isLinkDynamic !== undefined) linkBtn.isLinkDynamic = isLinkDynamic;
        if (dynamicVariableLabel !== undefined)
          linkBtn.dynamicVariableLabel = dynamicVariableLabel;
      }
    },

    // Marketing only: carousel
    addCarouselCard(state) {
      const newCard = {
        header: state.carouselMediaType === "image" ? "image" : "video", // or "none" if you prefer
        headerData: "",
        body: "",
        bodyVariables: [],
        buttons: [],
      };

      // If you want each new card to start with the current # of buttons:
      const num = state.carouselNumButtons;
      if (num >= 1) {
        newCard.buttons.push({ type: state.carouselBtnOneType, label: "" });
      }
      if (num === 2) {
        // add second
        newCard.buttons.push({ type: state.carouselBtnTwoType, label: "" });
      }

      state.carouselCards.push(newCard);
    },

    // If user changes carouselMediaType => apply to all existing cards
    updateCarouselSettings(state, action) {
      const { field, value } = action.payload;
      state[field] = value;

      // e.g. if user changed `carouselMediaType` from "image" -> "video", update all cards' `header`
      if (field === "carouselMediaType") {
        state.carouselCards.forEach((card) => {
          if (value === "image" || value === "video") {
            card.header = value;
          }
        });
      }

      // If user changes carouselNumButtons => adjust existing cards' button array
      if (field === "carouselNumButtons") {
        const newNum = parseInt(value, 10);
        state.carouselCards.forEach((card) => {
          const currLen = card.buttons.length;
          // If newNum = 1 => keep only first button
          if (newNum === 1 && currLen > 1) {
            card.buttons.splice(1); // remove everything beyond index 0
          } else if (newNum === 2 && currLen < 2) {
            // add second button if missing
            card.buttons.push({
              type: state.carouselBtnTwoType,
              label: "",
              link: "",
              countryCode: "",
            });
          }
        });
      }

      // If user changes carouselBtnOneType => update the first button in each card
      if (field === "carouselBtnOneType") {
        state.carouselCards.forEach((card) => {
          if (card.buttons[0]) {
            card.buttons[0].type = value; // preserve label? or reset? up to you
          }
        });
      }

      // If user changes carouselBtnTwoType => update second button in each card
      if (field === "carouselBtnTwoType") {
        state.carouselCards.forEach((card) => {
          if (card.buttons[1]) {
            card.buttons[1].type = value;
          }
        });
      }
    },
    removeCarouselCard(state, action) {
      state.carouselCards.splice(action.payload, 1);
    },
    updateCarouselCardField(state, action) {
      const { index, field, value } = action.payload;
      const card = state.carouselCards[index];
      if (card) {
        card[field] = value;
      }
    },

    // Manage placeholders for each card's body
    setCarouselCardBodyVariables(state, action) {
      const { index, bodyVariables } = action.payload;
      const card = state.carouselCards[index];
      if (card) {
        card.bodyVariables = bodyVariables;
      }
    },

    addButtonToCarouselCard(state, action) {
      const { cardIndex, buttonType } = action.payload;
      const card = state.carouselCards[cardIndex];
      if (!card) return;
      if (buttonType === "quickReply") {
        card.buttons.push({ type: "quickReply", label: "" });
      } else if (buttonType === "link") {
        card.buttons.push({ type: "link", label: "", link: "" });
      } else if (buttonType === "phone") {
        card.buttons.push({
          type: "phone",
          label: "",
          countryCode: "",
          phoneNumber: "",
        });
      }
    },
    removeButtonFromCarouselCard(state, action) {
      const { cardIndex, btnIndex } = action.payload;
      const card = state.carouselCards[cardIndex];
      if (card) {
        card.buttons.splice(btnIndex, 1);
      }
    },
    updateCarouselCardButtonField(state, action) {
      const { cardIndex, btnIndex, field, value } = action.payload;
      const card = state.carouselCards[cardIndex];
      if (card && card.buttons[btnIndex]) {
        card.buttons[btnIndex][field] = value;
      }
    },

    // Clear out everything except selectedCategory (optional)
    clearAllButtonsAndData(state) {
      const oldCategory = state.selectedCategory;
      Object.assign(state, {
        ...initialState,
        selectedCategory: oldCategory,
      });
    },
    // Clear out buttons and carousel cards only
    clearAllButtons(state) {
      state.buttons = {
        quickReplies: [],
        phoneButtons: [],
        linkButtons: [],
      };
      state.carouselCards = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTemplate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        // Store the full response for displaying messages
        state.responseData = action.payload;
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(uploadTemplateMedia.pending, (state) => {
        state.mediaUploadStatus = "loading";
        state.mediaUploadError = null;
      })
      .addCase(uploadTemplateMedia.fulfilled, (state) => {
        state.mediaUploadStatus = "succeeded";
        state.mediaUploadError = null;
      })
      .addCase(uploadTemplateMedia.rejected, (state, action) => {
        state.mediaUploadStatus = "failed";
        state.mediaUploadError = action.payload;
      });
  },
});

export const {
  setSelectedCategory,
  resetToDefaults,
  startEditTemplate,
  updateFormData,
  updateFieldsFlagAndMsg,
  setShouldCreate,
  setButtonsMode,
  // Common Buttons
  addQuickReply,
  removeQuickReply,
  updateQuickReply,
  addPhoneButton,
  removePhoneButton,
  updatePhoneButton,
  addLinkButton,
  removeLinkButton,
  updateLinkButton,
  // Carousel
  addCarouselCard,
  removeCarouselCard,
  updateCarouselCardField,
  setCarouselCardBodyVariables,
  addButtonToCarouselCard,
  removeButtonFromCarouselCard,
  updateCarouselCardButtonField,
  updateCarouselSettings,
  clearAllButtonsAndData,
  clearAllButtons,
} = templateCreationSlice.actions;

export default templateCreationSlice.reducer;
